/**
 * LLM Chat Completions — POST /.cloud/llm/chat/completions (#89098)
 *
 * Supports streaming (SSE) only. The API contract requires `stream: true`;
 * non-streaming is not supported. The SDK rejects `stream !== true` before
 * any network request is issued.
 *
 * Uses the shared fetch injected by `WorkBuddyCloudClient` — never
 * constructs its own fetch or adds Authorization/ModelHub headers.
 */

import { CloudOpenAIError } from './errors.js';
import { httpError } from './models.js';
import { iterSSEEvents, SSE_DONE } from './sse.js';
import type {
    ChatCompletionChunk,
    ChatCompletionCreateParams,
    ChatCompletionCreateParamsStreaming,
} from './types.js';

/**
 * Extract error from an SSE `event: error` data payload.
 *
 * The server sends:
 *   event: error
 *   data: { "error": { "message": "...", "type": "...", "param": null, "code": "..." } }
 *
 * `requestId` is the `X-Request-Id` from the streaming response header; it is
 * attached to the resulting `CloudOpenAIError` so in-stream failures stay
 * correlatable with server logs.
 */
function extractSSEError(data: string, requestId?: string): CloudOpenAIError {
    const init: import('./errors.js').CloudOpenAIErrorInit = requestId ? { requestId } : {};
    try {
        const parsed = JSON.parse(data);
        if (parsed && typeof parsed === 'object') {
            const errorObj = parsed.error ?? parsed;
            if (errorObj && typeof errorObj === 'object') {
                return new CloudOpenAIError(
                    {
                        message: typeof errorObj.message === 'string' ? errorObj.message : 'Stream error',
                        type: typeof errorObj.type === 'string' ? errorObj.type : 'server_error',
                        param: errorObj.param === undefined ? null : errorObj.param,
                        code: errorObj.code === undefined ? null : errorObj.code,
                    },
                    init,
                );
            }
        }
    } catch {
        // Not JSON
    }
    return new CloudOpenAIError(
        {
            message: data || 'Stream error',
            type: 'server_error',
            param: null,
            code: null,
        },
        init,
    );
}

/** LLM chat completions namespace. */
export class ChatCompletionsAPI {
    constructor(
        private readonly baseUrl: string,
        private readonly fetch: typeof globalThis.fetch,
    ) {}

    /**
     * Create a chat completion.
     *
     * The API only supports streaming. `input.stream` must be `true`;
     * any other value (or omitting `stream`) is rejected before a network
     * request is sent. Returns an async iterable of `ChatCompletionChunk`
     * objects decoded from the SSE stream.
     */
    create(input: ChatCompletionCreateParamsStreaming): AsyncIterable<ChatCompletionChunk>;
    create(input: ChatCompletionCreateParams): AsyncIterable<ChatCompletionChunk>;
    create(input: ChatCompletionCreateParams): AsyncIterable<ChatCompletionChunk> {
        if (input.stream !== true) {
            throw new CloudOpenAIError({
                message: 'stream must be true; non-streaming chat completions are not supported',
                type: 'invalid_request_error',
                param: 'stream',
                code: 'request_stream_required',
            });
        }
        return this.createStreaming(input as ChatCompletionCreateParamsStreaming);
    }

    /**
     * Streaming chat completion — returns an async generator of chunks.
     *
     * Reads the SSE stream incrementally, yielding ChatCompletionChunk objects.
     * `stream_options` is passed through unchanged when present.
     *
     * The `X-Request-Id` response header is captured and attached (as
     * `requestId`) to every `CloudOpenAIError` thrown from within the stream:
     * `event: error`, chunk-embedded error, JSON parse failure, and missing
     * `[DONE]` interruption. Non-2xx HTTP errors continue to go through
     * `httpError`, which independently extracts `X-Request-Id`.
     *
     * Throws CloudOpenAIError on:
     * - `event: error` in the stream
     * - Stream ends without `[DONE]` (gateway_stream_interrupted)
     * - JSON parse errors in data frames
     * - Caller abort (AbortSignal) — exits silently, no throw
     */
    private async *createStreaming(
        input: ChatCompletionCreateParamsStreaming,
    ): AsyncGenerator<ChatCompletionChunk, void, unknown> {
        const { signal, conversationId, ...body } = input;
        body.stream = true;

        let response: Response;
        try {
            response = await this.fetch(`${this.baseUrl}/chat/completions`, {
                method: 'POST',
                headers: {
                    Accept: 'text/event-stream',
                    'Content-Type': 'application/json',
                    ...(conversationId !== undefined ? { 'X-Conversation-ID': conversationId } : {}),
                },
                body: JSON.stringify(body),
                signal,
            });
        } catch (err) {
            if (err instanceof Error && err.name === 'AbortError') {
                return;
            }
            throw new CloudOpenAIError(
                {
                    message: `Network error: ${(err as Error)?.message ?? 'unknown'}`,
                    type: 'server_error',
                    param: null,
                    code: 'gateway_network_error',
                },
                { cause: err },
            );
        }

        // Non-2xx on a streaming request still returns a body — convert to error.
        if (!response.ok) {
            throw await httpError(response);
        }

        if (!response.body) {
            throw new CloudOpenAIError(
                {
                    message: 'Response body is empty — streaming not supported',
                    type: 'server_error',
                    param: null,
                    code: 'gateway_invalid_response',
                },
                { requestId: response.headers.get('x-request-id') ?? undefined },
            );
        }

        // Capture X-Request-Id from the streaming response header so all
        // in-stream errors (event:error, embedded error, JSON parse failure,
        // missing [DONE]) stay correlatable with server-side logs.
        const requestId = response.headers.get('x-request-id') ?? undefined;

        let receivedDone = false;
        let threw = false;

        try {
            for await (const sseEvent of iterSSEEvents(response.body, signal)) {
                // Check for named error event
                if (sseEvent.event === 'error') {
                    threw = true;
                    throw extractSSEError(sseEvent.data, requestId);
                }

                // Default event: data frame
                if (sseEvent.event === null || sseEvent.event === '') {
                    if (sseEvent.data === SSE_DONE) {
                        receivedDone = true;
                        return;
                    }

                    if (!sseEvent.data) {
                        // Empty data frame, skip
                        continue;
                    }

                    let chunk: ChatCompletionChunk;
                    try {
                        chunk = JSON.parse(sseEvent.data) as ChatCompletionChunk;
                    } catch {
                        threw = true;
                        throw new CloudOpenAIError(
                            {
                                message: `Failed to parse SSE data: ${sseEvent.data}`,
                                type: 'server_error',
                                param: null,
                                code: 'gateway_invalid_response',
                            },
                            { requestId },
                        );
                    }

                    // Check for error embedded in the chunk (some servers do this)
                    if (chunk && typeof chunk === 'object' && 'error' in chunk) {
                        threw = true;
                        const errObj = (chunk as unknown as { error: Record<string, unknown> }).error;
                        throw new CloudOpenAIError(
                            {
                                message: typeof errObj?.message === 'string' ? errObj.message : 'Stream error',
                                type: typeof errObj?.type === 'string' ? errObj.type : 'server_error',
                                param: errObj?.param === undefined ? null : (errObj.param as string | null),
                                code: errObj?.code === undefined ? null : (errObj.code as string | null),
                            },
                            { requestId },
                        );
                    }

                    yield chunk;
                }
            }
        } catch (err) {
            // AbortError: caller cancelled, exit silently
            if (err instanceof Error && err.name === 'AbortError') {
                return;
            }
            threw = true;
            throw err;
        }

        // Stream ended normally (reader returned done) but without [DONE].
        if (!receivedDone && !threw && !signal?.aborted) {
            throw new CloudOpenAIError(
                {
                    message: '流式响应中断。',
                    type: 'server_error',
                    param: null,
                    code: 'gateway_stream_interrupted',
                },
                { requestId },
            );
        }
    }
}
